import { GamePassBalanceCard } from './game-pass-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { isSteamClient } from '@/lib/steam'
import { useTRPC } from '@/lib/trpc'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Clock,
  CreditCard,
  RefreshCw,
  Wallet,
} from 'lucide-react'
import { toast } from 'sonner'

function formatCredits(microdollars: number) {
  return `$${(microdollars / 1_000_000).toFixed(2)}`
}

function getReasonLabel(
  reason: string | null,
  t: (key: string) => string,
): string {
  if (!reason)
    return t('auth.dashboard.creditHistoryUnknown')

  const normalizedReason = reason.toLowerCase()
  if (normalizedReason === 'purchase' || normalizedReason === 'steam_purchase')
    return t('auth.dashboard.creditHistoryOneTimeTopup')
  if (normalizedReason.includes('refund'))
    return t('auth.dashboard.creditHistoryRefund')

  return reason.replaceAll('_', ' ')
}

export function SubscriptionTab() {
  const { user } = useAuth()
  const { t } = useI18n()
  const trpc = useTRPC()

  const { data: status, isLoading } = useQuery({
    ...trpc.billingRouter.getBalanceStatus.queryOptions(),
    enabled: !!user,
  })

  const { data: historyData, isLoading: isHistoryLoading } = useQuery({
    ...trpc.billingRouter.getCreditHistory.queryOptions(),
    enabled: !!user,
  })

  const { mutateAsync: buyPack, isPending: isBuyingPack } = useMutation(
    trpc.billingRouter.createCheckoutSession.mutationOptions(),
  )

  const handleTopUp = async (packId: string) => {
    if (isSteamClient() && window.mentissSteam) {
      try {
        const { orderId } = await window.mentissSteam.initPurchase(packId)
        if (orderId) {
          toast.success(
            t('subscription.steamPurchaseInitiated')
            || 'Purchase initiated — approve in Steam overlay',
          )
        }
      }
      catch (err) {
        console.error('[Steam] Purchase error:', err)
        toast.error(t('subscription.failedCheckout'))
      }
      return
    }

    try {
      const { url } = await buyPack({ packId })
      if (url)
        window.location.assign(url)
    }
    catch {
      toast.error(t('subscription.failedCheckout'))
    }
  }

  const totalAvailable = status?.bankCredit ?? 0
  const inputPercent
    = status && status.totalPurchased > 0
      ? Math.round((status.inputCostUsed / status.totalPurchased) * 100)
      : 0
  const outputPercent
    = status && status.totalPurchased > 0
      ? Math.round((status.outputCostUsed / status.totalPurchased) * 100)
      : 0
  const remainingPercent = Math.max(0, 100 - inputPercent - outputPercent)
  const creditEntries = historyData?.entries ?? []

  return (
    <div className="space-y-6">
      { }
      {status ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('subscription.title')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            { }
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border bg-muted/30 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Wallet className="size-4 text-emerald-400" />
                  <span>{t('subscription.bankCredit')}</span>
                </div>
                <p className="mt-1 text-2xl font-bold text-emerald-400">
                  {formatCredits(totalAvailable)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('subscription.bankCreditDesc')}
                </p>
              </div>
              <div className="rounded-lg border bg-muted/30 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CreditCard className="size-4 text-sky-400" />
                  <span>{t('subscription.totalSpent')}</span>
                </div>
                <p className="mt-1 text-2xl font-bold">
                  {formatCredits(status.totalCharged)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('subscription.totalSpentDesc')}
                </p>
              </div>
            </div>

            { }
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('subscription.usage')}
                </span>
                <span className="font-medium">
                  {formatCredits(status.totalCharged)} /{' '}
                  {formatCredits(status.totalPurchased)}
                </span>
              </div>
              <div className="h-4 w-full overflow-hidden rounded-full bg-muted">
                <div className="flex size-full">
                  { }
                  <div
                    className="h-full bg-sky-500 transition-all duration-500"
                    style={{ width: `${inputPercent}%` }}
                    title={`${t('subscription.input')}: ${formatCredits(status.inputCostUsed)}`}
                  />
                  { }
                  <div
                    className="h-full bg-violet-500 transition-all duration-500"
                    style={{ width: `${outputPercent}%` }}
                    title={`${t('subscription.output')}: ${formatCredits(status.outputCostUsed)}`}
                  />
                  { }
                  <div
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${remainingPercent}%` }}
                    title={`${t('subscription.remaining')}: ${formatCredits(Math.max(0, totalAvailable))}`}
                  />
                </div>
              </div>
              { }
              <div className="flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:gap-4">
                <div className="flex items-center gap-1">
                  <div className="size-2.5 rounded-full bg-sky-500" />
                  <span>
                    {t('subscription.input')}:{' '}
                    {formatCredits(status.inputCostUsed)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="size-2.5 rounded-full bg-violet-500" />
                  <span>
                    {t('subscription.output')}:{' '}
                    {formatCredits(status.outputCostUsed)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="size-2.5 rounded-full bg-emerald-500" />
                  <span>
                    {t('subscription.remaining')}:{' '}
                    {formatCredits(Math.max(0, totalAvailable))}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground" aria-busy={isLoading}>
            {t(isLoading ? 'subscription.loadingStatus' : 'gamePasses.cashUnavailable')}
          </CardContent>
        </Card>
      )}

      <GamePassBalanceCard />

      { }
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="size-5" />
            {t('subscription.oneTimeTopUp')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('subscription.topUpDescription')}
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              { id: 'starter', price: 20, credits: 20 },
              { id: 'pro', price: 60, credits: 60 },
              { id: 'max', price: 100, credits: 120, bestValue: true },
            ].map(pack => (
              <div
                key={pack.id}
                className={`relative flex flex-col rounded-xl border p-5 transition-all ${
                  pack.bestValue
                    ? 'border-amber-500/50 bg-amber-500/5 shadow-lg shadow-amber-500/10'
                    : 'border-border hover:border-muted-foreground/50'
                }`}
              >
                {pack.bestValue && (
                  <Badge className="absolute -top-2.5 left-3 bg-amber-500 text-xs text-black">
                    {t('subscription.bestValue')}
                  </Badge>
                )}
                <p className="text-2xl font-bold">${pack.price}</p>
                <p className="mt-1 text-sm font-medium text-emerald-400">
                  ${pack.credits} {t('subscription.inCredits')}
                </p>
                {pack.price !== pack.credits && (
                  <p className="mt-1 text-xs text-amber-400">
                    +
                    {Math.round(
                      ((pack.credits - pack.price) / pack.price) * 100,
                    )}
                    % bonus
                  </p>
                )}
                <div className="mt-auto pt-4">
                  <Button
                    className="w-full"
                    size="sm"
                    variant={pack.bestValue ? 'default' : 'outline'}
                    disabled={isBuyingPack}
                    onClick={() => handleTopUp(pack.id)}
                  >
                    {t('subscription.buyNow')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="size-5" />
            {t('auth.dashboard.creditHistoryTitle')}
          </CardTitle>
          <CardDescription>
            {t('auth.dashboard.creditHistoryDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isHistoryLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : creditEntries.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              {t('auth.dashboard.creditHistoryEmpty')}
            </div>
          ) : (
            <div className="space-y-1">
              <div className="space-y-2 sm:hidden">
                {creditEntries.map((entry) => {
                  const isPositive = entry.creditsDelta > 0
                  const isNegative = entry.creditsDelta < 0

                  return (
                    <div
                      key={entry.id}
                      className="flex items-start justify-between gap-3 rounded-md border p-3"
                    >
                      <div className="flex min-w-0 items-start gap-2">
                        {isPositive ? (
                          <ArrowUpCircle className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                        ) : (
                          <ArrowDownCircle className="mt-0.5 size-4 shrink-0 text-red-400" />
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {getReasonLabel(entry.reason, t)}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {new Date(entry.createdAt).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`shrink-0 text-right text-sm font-medium tabular-nums ${
                          isPositive
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : isNegative
                              ? 'text-red-500 dark:text-red-400'
                              : ''
                        }`}
                      >
                        {isPositive ? '+' : ''}
                        {formatCredits(entry.creditsDelta)}
                      </span>
                    </div>
                  )
                })}
              </div>

              <div className="hidden grid-cols-[1fr_100px_130px] items-center gap-2 border-b px-2 pb-2 text-xs font-medium text-muted-foreground sm:grid sm:grid-cols-[1fr_120px_160px]">
                <span>{t('auth.dashboard.creditHistoryColEvent')}</span>
                <span className="text-right">
                  {t('auth.dashboard.creditHistoryColAmount')}
                </span>
                <span className="text-right">
                  {t('auth.dashboard.creditHistoryColDate')}
                </span>
              </div>

              <div className="hidden space-y-1 sm:block">
                {creditEntries.map((entry) => {
                  const isPositive = entry.creditsDelta > 0
                  const isNegative = entry.creditsDelta < 0

                  return (
                    <div
                      key={entry.id}
                      className="grid grid-cols-[1fr_100px_130px] items-center gap-2 rounded-md px-2 py-2.5 transition-colors hover:bg-muted/50 sm:grid-cols-[1fr_120px_160px]"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        {isPositive ? (
                          <ArrowUpCircle className="size-4 shrink-0 text-emerald-500" />
                        ) : (
                          <ArrowDownCircle className="size-4 shrink-0 text-red-400" />
                        )}
                        <span className="truncate text-sm">
                          {getReasonLabel(entry.reason, t)}
                        </span>
                      </div>

                      <span
                        className={`text-right text-sm font-medium tabular-nums ${
                        isPositive
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : isNegative
                            ? 'text-red-500 dark:text-red-400'
                            : ''
                      }`}
                      >
                        {isPositive ? '+' : ''}
                        {formatCredits(entry.creditsDelta)}
                      </span>

                      <span className="text-right text-xs tabular-nums text-muted-foreground">
                        {new Date(entry.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
